/**
 * AI Chat Page - Nurin Healthcare Analytics Assistant
 *
 * Features:
 * - Natural language queries about patients, invoices, treatments
 * - Streaming responses with Vercel AI SDK
 * - Artifact generation (charts, tables)
 * - Dual panel mode when artifacts are present
 * - Chat history via main sidebar (Google AI Studio style)
 * - Bilingual support (English/Bahasa Malaysia)
 */

import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useChat } from 'ai/react'
import * as React from 'react'
import { PanelRightOpen, PanelLeft, X } from 'lucide-react'
import { useBranch } from '@/providers/branch-provider'
import { useSidebar } from '@/providers/sidebar-provider'
import { ChatPanel } from '@/components/chat/chat-panel'
import { ChatHistoryList } from '@/components/chat/chat-history-list'
import { ArtifactPanel, type Artifact } from '@/components/chat/artifact-panel'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useChatSession } from '@/hooks/useChatSessions'
import { useQueryClient } from '@tanstack/react-query'
import { chatSessionKeys } from '@/hooks/useChatSessions'
import { useCurrentUser } from '@/hooks/useAuth'
import { useModels, getSavedModel, saveSelectedModel } from '@/hooks/useModels'
import type { ArtifactCardData } from '@/components/chat/artifact-card'

// API base URL for chat endpoint
const CHAT_API_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1/chat`
  : '/api/v1/chat'

// Define search params schema
type ChatSearch = {
  session?: string
  new?: string
  history?: string
}

export const Route = createFileRoute('/chat')({
  component: ChatPage,
  validateSearch: (search: Record<string, unknown>): ChatSearch => {
    return {
      session: search.session as string | undefined,
      new: search.new as string | undefined,
      history: search.history as string | undefined,
    }
  },
})

function ChatPage() {
  const { selectedBranch } = useBranch()
  const { setOpenMobile } = useSidebar()
  const queryClient = useQueryClient()
  const search = useSearch({ from: '/chat' })
  const { data: currentUser, isPending: isUserLoading } = useCurrentUser()

  // Check view mode
  const isHistoryView = search.history === 'true'
  const sessionIdFromUrl = search.session ? parseInt(search.session) : null
  const isNewChat = !!search.new

  // Session state - always call hooks in same order
  const [currentSessionId, setCurrentSessionId] = React.useState<number | null>(
    isNewChat ? null : sessionIdFromUrl
  )

  // Artifact state
  // allArtifacts = ALL artifacts in session (for panel display)
  // currentMessageArtifacts = artifacts from CURRENT streaming message only (for card display)
  const [allArtifacts, setAllArtifacts] = React.useState<Artifact[]>([])
  const [currentMessageArtifacts, setCurrentMessageArtifacts] = React.useState<Artifact[]>([])
  const [showPanel, setShowPanel] = React.useState(true)

  // Flash effect for visual feedback when starting new chat
  const [isFlashing, setIsFlashing] = React.useState(false)

  // Model state
  const { data: modelsData } = useModels()
  const [selectedModel, setSelectedModel] = React.useState<string | null>(() => {
    return getSavedModel()
  })

  // Set default model when models load
  React.useEffect(() => {
    if (modelsData?.default && !selectedModel) {
      setSelectedModel(modelsData.default)
    }
  }, [modelsData?.default, selectedModel])

  // Handle model selection
  const handleSelectModel = React.useCallback((modelId: string) => {
    setSelectedModel(modelId)
    saveSelectedModel(modelId)
  }, [])

  // Load session data when session ID changes
  const { data: sessionData } = useChatSession(currentSessionId)

  // Update session ID when URL changes
  React.useEffect(() => {
    if (isNewChat) {
      setCurrentSessionId(null)
      setAllArtifacts([])
      setCurrentMessageArtifacts([])
    } else if (sessionIdFromUrl) {
      setCurrentSessionId(sessionIdFromUrl)
      setAllArtifacts([])
      setCurrentMessageArtifacts([])
    }
  }, [sessionIdFromUrl, isNewChat])

  // Generate unique chat ID based on new chat timestamp or session ID
  // This forces useChat to create fresh state when starting new chat
  const chatId = React.useMemo(() => {
    if (search.new) return `new-${search.new}`
    if (currentSessionId) return `session-${currentSessionId}`
    return 'default'
  }, [search.new, currentSessionId])

  // Initialize useChat hook with API configuration
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    data: streamData,
    setMessages,
    reload,
    stop,
  } = useChat({
    id: chatId, // Unique ID forces fresh state on new chat
    api: CHAT_API_URL,
    body: {
      branchId: selectedBranch?.id,
      sessionId: currentSessionId,
      modelId: selectedModel,
      userId: currentUser?.id?.toString(), // Associate chat with user
    },
    onError: (err) => {
      console.error('[Chat] Error:', err)
    },
    onFinish: () => {
      // Refresh session list after chat completes
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() })
    },
  })

  // Handle retry (regenerate last response)
  const handleRetry = React.useCallback(() => {
    reload()
  }, [reload])

  // Handle artifact click (open in panel)
  const handleClickArtifact = React.useCallback((artifact: ArtifactCardData) => {
    setShowPanel(true)
    // Find the artifact in our list and scroll to it
    const index = allArtifacts.findIndex(a => a.id === artifact.id)
    if (index >= 0) {
      // Could scroll to it in the panel if needed
    }
  }, [allArtifacts])

  // Load messages when session data is fetched
  React.useEffect(() => {
    if (sessionData?.messages) {
      // Convert DB messages to useChat format
      const chatMessages = sessionData.messages.map((m) => ({
        id: m.id.toString(),
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.content,
      }))
      setMessages(chatMessages)

      // Load artifacts from session (for panel display only, not cards)
      const sessionArtifacts: Artifact[] = []
      for (const msg of sessionData.messages) {
        if (msg.artifacts && Array.isArray(msg.artifacts)) {
          for (const artifact of msg.artifacts) {
            const a = artifact as Record<string, unknown>
            sessionArtifacts.push({
              id: `artifact-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              type: a.type as 'chart' | 'table' | 'export',
              title: (a.title as string) || 'Untitled',
              data: a,
              generatedAt: msg.createdAt,
            })
          }
        }
      }
      if (sessionArtifacts.length > 0) {
        setAllArtifacts(sessionArtifacts)
        // Don't set currentMessageArtifacts - these are historical, not current
        setShowPanel(true)
      }
    }
  }, [sessionData, setMessages])

  // Track previous new chat timestamp to detect changes
  const prevNewRef = React.useRef<string | undefined>(undefined)
  const isFirstLoadRef = React.useRef(true)

  // Clear messages for new chat with visual flash
  React.useEffect(() => {
    // Only trigger if search.new exists AND it's different from before
    if (search.new && search.new !== prevNewRef.current) {
      const wasFirstLoad = isFirstLoadRef.current
      isFirstLoadRef.current = false
      prevNewRef.current = search.new

      // Reset state
      setMessages([])
      setAllArtifacts([])
      setCurrentMessageArtifacts([])
      setCurrentSessionId(null)

      // Only flash on subsequent new chats, not on first load
      if (!wasFirstLoad) {
        setIsFlashing(true)
        const timer = setTimeout(() => setIsFlashing(false), 150)
        return () => clearTimeout(timer)
      }
    }
  }, [search.new, setMessages])

  // Track which stream data items we've already processed
  const processedItemsRef = React.useRef<Set<string>>(new Set())

  // Reset processed items when starting new chat
  React.useEffect(() => {
    if (messages.length === 0) {
      processedItemsRef.current.clear()
    }
  }, [messages.length])

  // Helper to process a single data item
  const processDataItem = (data: Record<string, unknown>, itemKey: string, newArtifacts: Artifact[]) => {
    if (data.type === 'artifact') {
      console.log('[Chat] ARTIFACT FOUND:', data.artifactType)
      processedItemsRef.current.add(itemKey)

      const artifactType = data.artifactType as 'chart' | 'table' | 'export'
      let artifactData: Record<string, unknown>

      try {
        artifactData = typeof data.data === 'string'
          ? JSON.parse(data.data)
          : data.data as Record<string, unknown>
        console.log('[Chat] Artifact data:', artifactData?.title, artifactData?.chartType)
      } catch (e) {
        console.error('[Chat] Failed to parse artifact:', e)
        return
      }

      newArtifacts.push({
        id: `artifact-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: artifactType,
        title: artifactData.title as string || 'Untitled',
        data: artifactData,
        generatedAt: new Date().toISOString(),
      })
    }

    // Extract session ID from metadata
    if (data.type === 'metadata' && data.sessionId && !currentSessionId) {
      setCurrentSessionId(data.sessionId as number)
    }
  }

  // Watch for artifacts and session ID during streaming
  React.useEffect(() => {
    if (!streamData || streamData.length === 0) return

    const newArtifacts: Artifact[] = []

    for (let i = 0; i < streamData.length; i++) {
      const item = streamData[i]
      const itemKey = `${i}-${JSON.stringify(item).slice(0, 100)}`

      // Skip if already processed
      if (processedItemsRef.current.has(itemKey)) continue

      // Handle both direct objects and arrays (Vercel AI SDK wraps in arrays)
      if (Array.isArray(item)) {
        console.log('[Chat] Item is array with', item.length, 'elements')
        for (const subItem of item) {
          if (typeof subItem === 'object' && subItem !== null) {
            processDataItem(subItem as Record<string, unknown>, itemKey, newArtifacts)
          }
        }
      } else if (typeof item === 'object' && item !== null) {
        const data = item as Record<string, unknown>
        processDataItem(data, itemKey, newArtifacts)
      }
    }

    if (newArtifacts.length > 0) {
      console.log('[Chat] Adding', newArtifacts.length, 'artifacts')
      // Add to both: allArtifacts (panel) and currentMessageArtifacts (card display)
      setAllArtifacts((prev) => [...prev, ...newArtifacts])
      setCurrentMessageArtifacts((prev) => [...prev, ...newArtifacts])
      setShowPanel(true)
    }
  }, [streamData, currentSessionId])

  // Handle artifact export
  const handleExport = React.useCallback((artifact: Artifact, format: 'csv' | 'excel') => {
    // Parse the artifact data
    const data = typeof artifact.data === 'string'
      ? JSON.parse(artifact.data)
      : artifact.data

    if (format === 'csv') {
      exportToCSV(data, artifact.title)
    } else {
      exportToExcel(data, artifact.title)
    }
  }, [])

  // Handle input change from textarea
  const onInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleInputChange({
        target: { value: e.target.value },
      } as React.ChangeEvent<HTMLInputElement>)
    },
    [handleInputChange]
  )

  // Handle form submission
  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (input.trim()) {
        // Clear current message artifacts when sending new message
        // (old artifacts stay in allArtifacts for panel)
        setCurrentMessageArtifacts([])
        handleSubmit(e)
      }
    },
    [input, handleSubmit]
  )

  const hasArtifacts = allArtifacts.length > 0
  // State for mobile artifact sheet
  const [mobileArtifactOpen, setMobileArtifactOpen] = React.useState(false)

  // Open mobile artifact sheet when new artifact arrives
  React.useEffect(() => {
    if (hasArtifacts) {
      setMobileArtifactOpen(true)
    }
  }, [allArtifacts.length])

  // Render history view if requested (after all hooks are called)
  if (isHistoryView) {
    return <ChatHistoryList />
  }

  return (
    <div className="flex h-[calc(100vh-1px)] bg-page relative">
      {/* Mobile Sidebar Toggle - Fixed at top left */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpenMobile(true)}
        className="lg:hidden fixed top-3 left-3 z-50 h-9 w-9 bg-card/80 backdrop-blur-sm border border-border shadow-sm"
        aria-label="Open navigation menu"
      >
        <PanelLeft className="h-5 w-5" />
      </Button>

      {/* Chat Panel - Full width on mobile, half on desktop when artifacts panel is open */}
      <div
        className={cn(
          'flex flex-col flex-1 transition-opacity duration-150 ease-in-out',
          isFlashing ? 'opacity-0' : 'opacity-100',
          // On lg+, shrink to half when artifacts exist AND panel is open
          hasArtifacts && showPanel && 'lg:w-1/2 lg:border-r lg:border-border'
        )}
      >
        <ChatPanel
          messages={messages}
          input={input}
          onInputChange={onInputChange}
          onSubmit={onSubmit}
          onStop={stop}
          isLoading={isLoading}
          disabled={isUserLoading || !currentUser}
          error={error as Error | undefined}
          streamData={streamData as unknown[] | undefined}
          models={modelsData?.models || []}
          selectedModel={selectedModel}
          onSelectModel={handleSelectModel}
          onRetry={handleRetry}
          onClickArtifact={handleClickArtifact}
          artifacts={currentMessageArtifacts}
        />
      </div>

      {/* Desktop: Artifact Panel - Slides in when artifacts exist and panel is open */}
      {hasArtifacts && showPanel && (
        <div className="hidden lg:block w-1/2 animate-in slide-in-from-right duration-300">
          <ArtifactPanel
            artifacts={allArtifacts}
            onClose={() => setShowPanel(false)}
            onExport={handleExport}
          />
        </div>
      )}

      {/* Mobile: Artifact Sheet */}
      <Sheet open={mobileArtifactOpen && hasArtifacts} onOpenChange={setMobileArtifactOpen}>
        <SheetContent side="right" className="w-full sm:w-[400px] p-0 lg:hidden">
          <ArtifactPanel
            artifacts={allArtifacts}
            onClose={() => setMobileArtifactOpen(false)}
            onExport={handleExport}
          />
        </SheetContent>
      </Sheet>

      {/* Floating button to show artifacts when they exist but panel is closed */}
      {/* Mobile: show when mobileArtifactOpen is false */}
      {hasArtifacts && !mobileArtifactOpen && (
        <Button
          onClick={() => setMobileArtifactOpen(true)}
          size="icon"
          className="lg:hidden fixed bottom-24 right-4 h-12 w-12 rounded-full shadow-lg z-40"
        >
          <PanelRightOpen className="h-5 w-5" />
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground font-medium">
            {allArtifacts.length}
          </span>
        </Button>
      )}
      {/* Desktop: show when showPanel is false */}
      {hasArtifacts && !showPanel && (
        <Button
          onClick={() => setShowPanel(true)}
          size="icon"
          className="hidden lg:flex fixed bottom-8 right-8 h-12 w-12 rounded-full shadow-lg z-40"
        >
          <PanelRightOpen className="h-5 w-5" />
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground font-medium">
            {allArtifacts.length}
          </span>
        </Button>
      )}
    </div>
  )
}

// CSV Export helper
function exportToCSV(data: unknown, filename: string) {
  let csvContent = ''

  // Handle table data
  if (data && typeof data === 'object' && 'columns' in data && 'data' in data) {
    const tableData = data as {
      columns: { key: string; header: string }[]
      data: Record<string, unknown>[]
    }

    // Headers
    csvContent = tableData.columns.map((c) => c.header).join(',') + '\n'

    // Rows
    csvContent += tableData.data
      .map((row) =>
        tableData.columns
          .map((c) => {
            const value = row[c.key]
            // Escape commas and quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`
            }
            return value
          })
          .join(',')
      )
      .join('\n')
  }
  // Handle chart data
  else if (data && typeof data === 'object' && 'data' in data) {
    const chartData = (data as { data: { label: string; value: number }[] }).data
    csvContent = 'Label,Value\n'
    csvContent += chartData.map((d) => `${d.label},${d.value}`).join('\n')
  }

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename.replace(/[^a-z0-9]/gi, '_')}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Excel Export helper (simplified - uses CSV format that Excel can open)
function exportToExcel(data: unknown, filename: string) {
  // For now, use CSV as Excel can open it
  // TODO: Use xlsx library for proper Excel export
  exportToCSV(data, filename)
}
